import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Loader2, Edit3, Trash2, Save, Users, Check, Grid3x3 } from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import BulkAssignSquadClassModal from "./BulkAssignSquadClassModal";
import { confirmDialog } from "../utils/dialogs";

/**
 * SquadsManager — controlled-vocabulary master for the "Group / Squad"
 * field on every athlete (MJPT, AGAPE, Current Athletes, …).
 *
 * Rename here → propagates onto every athlete's `group` server-side so
 * the by-squad filter pills and rankings stay in sync. Retire = soft
 * delete: the athletes keep their group text, the squad just stops
 * appearing in the create-athlete dropdown.
 *
 * Admin only. Sits inside the UserMenu.
 */
export default function SquadsManager({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", notes: "" });
  const [showInactive, setShowInactive] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);

  useEscClose(open, onClose);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/squads?include_inactive=${showInactive}`);
      setItems(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load squads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open, showInactive]);

  const startAdd = () => {
    setEditingId(null);
    setForm({ name: "", notes: "" });
    setShowAdd(true);
    setError("");
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, notes: s.notes || "" });
    setShowAdd(true);
    setError("");
  };
  const cancel = () => { setShowAdd(false); setEditingId(null); setError(""); };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { setError("Squad name is required"); return; }
    try {
      if (editingId) {
        await api.put(`/squads/${editingId}`, {
          name, notes: form.notes || null,
        });
      } else {
        await api.post("/squads", {
          name, notes: form.notes || null,
        });
      }
      setShowAdd(false); setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const retire = async (s) => {
    if (!(await confirmDialog(`Retire "${s.name}"? Athletes keep this squad on their profile, but it will no longer show up in the new-athlete dropdown.`, { confirmLabel: "Retire", danger: true }))) return;
    try {
      await api.delete(`/squads/${s.id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Retire failed");
    }
  };

  const reactivate = async (s) => {
    try {
      await api.put(`/squads/${s.id}`, { active: true });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reactivate failed");
    }
  };

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-8"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={onClose}
      data-testid="squads-manager"
    >
      <div className="card w-full max-w-3xl mx-4 p-5 md:p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-100" data-testid="squads-close">
          <X size={20} />
        </button>

        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300">
            <Users size={20} />
          </div>
          <div>
            <div className="font-display text-xl text-slate-100">Squads master</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Controlled vocabulary for the &ldquo;Group / Squad&rdquo; field on every athlete.
              Rename here → propagates to every athlete automatically.
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-4 flex items-center gap-2">
          <button onClick={startAdd} className="btn-primary text-[12px]" data-testid="squads-add-btn">
            <Plus size={13} /> Add squad
          </button>
          <button onClick={() => setShowBulkAssign(true)} className="btn-ghost text-[12px]"
                  data-testid="squads-bulk-assign-btn"
                  title="Sailor-wise spreadsheet: bulk edit Squad + Class">
            <Grid3x3 size={13} /> Assign athletes…
          </button>
          <label className="text-[11px] text-slate-400 flex items-center gap-1.5 ml-auto cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                   className="accent-cyan-400" data-testid="squads-toggle-inactive" />
            Show retired
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px]">{error}</div>
        )}

        {showAdd && (
          <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Name *</div>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="form-input text-[12px]" placeholder="e.g. MJPT, AGAPE, Alumni" autoFocus
                       data-testid="squads-form-name" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Notes</div>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       className="form-input text-[12px]" placeholder="e.g. Ministry of Youth talent programme"
                       data-testid="squads-form-notes" />
              </div>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={cancel} className="btn-ghost text-[12px]" data-testid="squads-form-cancel">Cancel</button>
              <button onClick={save} className="btn-primary text-[12px]" data-testid="squads-form-save">
                <Save size={12} /> {editingId ? "Update" : "Add"}
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-[#1E304B] max-h-[52vh] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-[12px] flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-[11px]">
              No squads yet. Click &ldquo;Add squad&rdquo; to create the first one.
            </div>
          ) : (
            <ul>
              {items.map((s) => (
                <li key={s.id}
                    className={`border-b border-[#1E304B] last:border-b-0 px-3 py-2 flex items-center gap-2 ${s.active === false ? "opacity-50" : ""}`}
                    data-testid={`squads-row-${s.id}`}>
                  <Users size={14} className="text-cyan-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-100 font-mono-data flex items-center gap-2">
                      {s.name}
                      {s.active === false && (
                        <span className="text-[9px] uppercase tracking-widest px-1 rounded bg-slate-600/40 text-slate-300">retired</span>
                      )}
                    </div>
                    {s.notes && (
                      <div className="text-[10px] text-slate-500 truncate mt-0.5">{s.notes}</div>
                    )}
                  </div>
                  <button onClick={() => startEdit(s)} className="btn-ghost !p-1.5" title="Edit"
                          data-testid={`squads-edit-${s.id}`}>
                    <Edit3 size={12} />
                  </button>
                  {s.active === false ? (
                    <button onClick={() => reactivate(s)} className="btn-ghost !p-1.5 !text-emerald-300" title="Reactivate"
                            data-testid={`squads-reactivate-${s.id}`}>
                      <Check size={12} />
                    </button>
                  ) : (
                    <button onClick={() => retire(s)} className="btn-ghost !p-1.5 !text-rose-300" title="Retire"
                            data-testid={`squads-retire-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <BulkAssignSquadClassModal
        open={showBulkAssign}
        onClose={() => setShowBulkAssign(false)}
        onSaved={load}
      />
    </div>,
    document.body,
  );
}
